/**
 * <s-shipping-estimate> shows the live shipping options for the current cart
 * once the shopper enters an Indian PIN code. Rates come from Shopify's cart
 * shipping-rates endpoints, so they always match the store's shipping settings.
 *
 * Commands (dispatched via commandfor/command on descendant buttons):
 *   --estimate — Validates the [data-pincode] input and fetches rates.
 *
 * Element dataset:
 *   data-cart-url  — routes.cart_url from Liquid.
 *   data-country   — Country sent with the PIN code.
 *   data-currency  — ISO currency code used to format prices.
 *   data-locale    — Locale used to format prices.
 *   data-text-*    — Translated UI strings.
 *   data-mode      — "live" fetches cart rates; "static" lists Standard/Express/COD
 *                    from data-express-price / data-cod-price. Both show arrival dates
 *                    from data-days-standard / data-days-express (calendar days).
 *
 * Required child markup:
 *   [data-pincode] — PIN code input.
 *   [data-results] — Live region where results and messages are rendered.
 */
// India needs a state for shipping rates. Map the first three PIN digits to the state
// (India Post circles; specific ranges listed before the broad ones they sit inside).
const PIN_STATES = [
  [110, 110, 'Delhi'], [160, 160, 'Chandigarh'], [121, 136, 'Haryana'], [140, 159, 'Punjab'],
  [171, 177, 'Himachal Pradesh'], [194, 194, 'Ladakh'], [180, 193, 'Jammu and Kashmir'],
  [246, 249, 'Uttarakhand'], [262, 263, 'Uttarakhand'], [201, 285, 'Uttar Pradesh'],
  [301, 345, 'Rajasthan'], [360, 396, 'Gujarat'], [403, 403, 'Goa'], [400, 445, 'Maharashtra'],
  [450, 488, 'Madhya Pradesh'], [490, 497, 'Chhattisgarh'], [500, 509, 'Telangana'],
  [515, 535, 'Andhra Pradesh'], [560, 591, 'Karnataka'], [605, 605, 'Puducherry'],
  [600, 643, 'Tamil Nadu'], [670, 695, 'Kerala'], [737, 737, 'Sikkim'],
  [744, 744, 'Andaman and Nicobar Islands'], [700, 743, 'West Bengal'], [751, 770, 'Odisha'],
  [781, 788, 'Assam'], [790, 792, 'Arunachal Pradesh'], [793, 794, 'Meghalaya'], [795, 795, 'Manipur'],
  [796, 796, 'Mizoram'], [797, 798, 'Nagaland'], [799, 799, 'Tripura'], [814, 835, 'Jharkhand'],
  [800, 855, 'Bihar'],
];

function stateForPincode(pincode) {
  const prefix = Number(pincode.slice(0, 3));
  const match = PIN_STATES.find(([start, end]) => prefix >= start && prefix <= end);
  return match ? match[2] : '';
}

class ShippingEstimate extends HTMLElement {
  #ac;
  #requestAc;

  connectedCallback() {
    this.#ac = new AbortController();
    const { signal } = this.#ac;
    this.addEventListener('command', (event) => {
      if (event.command === '--estimate') this.#estimate();
    }, { signal });
    this.querySelector('[data-pincode]')?.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter') return;
      event.preventDefault();
      this.#estimate();
    }, { signal });
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#requestAc?.abort();
  }

  get #results() {
    return this.querySelector('[data-results]');
  }

  #message(text, isError = false) {
    const p = document.createElement('p');
    p.className = isError ? 'text-xs text-accent' : 'text-xs text-on-surface-secondary';
    p.textContent = text;
    this.#results.replaceChildren(p);
  }

  #formatPrice(amount) {
    const value = Number.parseFloat(amount);
    if (!Number.isFinite(value) || value === 0) return this.dataset.textFree;
    return new Intl.NumberFormat(this.dataset.locale || undefined, {
      style: 'currency',
      currency: this.dataset.currency,
    }).format(value);
  }

  async #estimate() {
    const input = this.querySelector('[data-pincode]');
    const pincode = input.value.replace(/\s+/g, '');
    if (!/^[1-9][0-9]{5}$/.test(pincode)) {
      input.setAttribute('aria-invalid', 'true');
      this.#message(this.dataset.textInvalid, true);
      input.focus();
      return;
    }
    input.removeAttribute('aria-invalid');

    // Static mode (product page): the cart may not hold this product yet, so list the store's standard options.
    if (this.dataset.mode === 'static') {
      this.#render(this.#staticOptions(), pincode, this.dataset.textStaticNote);
      return;
    }

    this.#requestAc?.abort();
    this.#requestAc = new AbortController();
    const { signal } = this.#requestAc;
    this.#message(this.dataset.textLoading);

    const params = new URLSearchParams({
      'shipping_address[zip]': pincode,
      'shipping_address[country]': this.dataset.country,
    });
    const state = stateForPincode(pincode);
    if (state) params.set('shipping_address[province]', state);
    const base = this.dataset.cartUrl.replace(/\/$/, '');

    try {
      const prepare = await fetch(`${base}/prepare_shipping_rates.json?${params}`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        signal,
      });
      if (prepare.status === 422) {
        this.#message(this.dataset.textInvalid, true);
        return;
      }
      if (!prepare.ok) throw new Error('prepare');

      let rates = null;
      for (let attempt = 0; attempt < 10 && rates === null; attempt++) {
        const response = await fetch(`${base}/async_shipping_rates.json?${params}`, {
          headers: { Accept: 'application/json' },
          signal,
        });
        if (!response.ok) throw new Error('rates');
        const data = await response.json();
        if (data && Array.isArray(data.shipping_rates)) {
          rates = data.shipping_rates;
        } else {
          await new Promise((resolve) => setTimeout(resolve, 500));
        }
      }
      if (rates === null) throw new Error('timeout');
      this.#render(rates.map((rate) => this.#liveOption(rate)), pincode);
    } catch (error) {
      if (signal.aborted) return;
      // Live rates unavailable: still show the standard options and dates rather than a dead end.
      this.#render(this.#staticOptions(), pincode, this.dataset.textStaticNote);
    }
  }

  #range(kind) {
    const [min, max] = (kind === 'express' ? this.dataset.daysExpress : this.dataset.daysStandard)
      .split(',')
      .map(Number);
    const format = new Intl.DateTimeFormat('en-IN', { day: 'numeric', month: 'short', timeZone: 'Asia/Kolkata' });
    const day = 86400000;
    const now = Date.now();
    return this.dataset.textArrives
      .replace('__FROM__', format.format(new Date(now + min * day)))
      .replace('__TO__', format.format(new Date(now + max * day)));
  }

  #priceText(value) {
    return Number(value) === 0 ? this.dataset.textFree : this.#formatPrice(value);
  }

  #liveOption(rate) {
    const raw = `${rate.presentment_name || rate.name || ''}`;
    let kind = 'standard';
    let name = this.dataset.textStandard;
    if (/express/i.test(raw)) {
      kind = 'express';
      name = this.dataset.textExpress;
    } else if (/\bcod\b|cash on delivery/i.test(raw)) {
      name = this.dataset.textCod;
    } else if (!/standard/i.test(raw)) {
      name = raw;
    }
    return { name, price: this.#priceText(rate.price), kind };
  }

  #staticOptions() {
    const options = [{ name: this.dataset.textStandard, price: this.dataset.textFree, kind: 'standard' }];
    const express = Number(this.dataset.expressPrice);
    if (express > 0) options.push({ name: this.dataset.textExpress, price: this.#formatPrice(express), kind: 'express' });
    const cod = Number(this.dataset.codPrice);
    if (cod > 0) {
      options.push({
        name: this.dataset.textCod,
        price: this.dataset.textCodFrom.replace('__AMOUNT__', this.#formatPrice(cod)),
        kind: 'standard',
      });
    }
    return options;
  }

  #render(options, pincode, note) {
    if (!options.length) {
      this.#message(this.dataset.textNoRates, true);
      return;
    }
    const heading = document.createElement('p');
    heading.className = 'text-xs text-on-surface-secondary mb-2';
    heading.textContent = this.dataset.textResults.replace('__PINCODE__', pincode);

    const list = document.createElement('ul');
    list.setAttribute('role', 'list');
    list.className = 'divide-y divide-border border border-border bg-surface';
    for (const option of options) {
      const li = document.createElement('li');
      li.className = 'flex items-start justify-between gap-4 px-3 py-2 text-sm';
      const text = document.createElement('span');
      text.className = 'flex flex-col';
      const name = document.createElement('span');
      name.className = 'text-on-surface';
      name.textContent = option.name;
      const date = document.createElement('span');
      date.className = 'text-xs text-on-surface-secondary';
      date.textContent = this.#range(option.kind);
      text.append(name, date);
      const price = document.createElement('span');
      price.className = `shrink-0 font-medium ${option.price === this.dataset.textFree ? 'text-accent' : 'text-on-surface'}`;
      price.textContent = option.price;
      li.append(text, price);
      list.append(li);
    }
    const parts = [heading, list];
    if (note) {
      const p = document.createElement('p');
      p.className = 'mt-2 text-xs text-on-surface-secondary';
      p.textContent = note;
      parts.push(p);
    }
    this.#results.replaceChildren(...parts);
  }
}

if (!customElements.get('s-shipping-estimate')) {
  customElements.define('s-shipping-estimate', ShippingEstimate);
}
